import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-hero-user-pill',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (isLoggedIn()) {
      <div class="user-pill">
        <span class="user-avatar">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </span>
        <span>{{ userName() ?? 'Authenticated User' }}</span>
        @if (displayRoleShort(); as roleShort) {
          <span class="user-role-pill" [title]="displayRoleFull() ?? ''">{{ roleShort }}</span>
        }
      </div>
    }
  `,
  styles: [`
    .user-pill {
      align-items: center;
      align-self: flex-start;
      background: color-mix(in srgb, var(--color-accent) 15%, transparent);
      border: 1px solid color-mix(in srgb, var(--color-accent) 28%, transparent);
      border-radius: 9999px;
      color: var(--color-accent-soft);
      display: flex;
      font-size: 0.82rem;
      font-weight: 600;
      gap: 0.45rem;
      padding: 0.28rem 0.75rem 0.28rem 0.38rem;
    }

    .user-avatar {
      align-items: center;
      background: color-mix(in srgb, var(--color-accent) 26%, transparent);
      border-radius: 50%;
      color: var(--color-accent-soft);
      display: flex;
      height: 22px;
      justify-content: center;
      width: 22px;
    }

    .user-role-pill {
      background: color-mix(in srgb, var(--color-accent) 25%, transparent);
      border: 1px solid color-mix(in srgb, var(--color-accent) 34%, transparent);
      border-radius: 9999px;
      color: var(--color-accent-soft);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      padding: 0.1rem 0.45rem;
      text-transform: uppercase;
    }
  `]
})
export class HeroUserPillComponent implements OnInit, OnDestroy {
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authService = inject(AuthService);
  private readonly destroying$ = new Subject<void>();

  readonly isLoggedIn = signal(false);
  readonly userName = signal<string | null>(null);
  readonly roles = signal<string[]>([]);

  readonly displayRoleFull = computed(() => {
    const roles = this.roles();
    if (roles.includes('SiteAdmin')) return 'SiteAdmin';
    if (roles.includes('OrgAdmin')) return 'OrgAdmin';
    if (roles.includes('EventPlanner')) return 'EventPlanner';
    if (roles.includes('TeamMember')) return 'TeamMember';
    return null;
  });

  readonly displayRoleShort = computed(() => {
    const fullRole = this.displayRoleFull();
    if (fullRole === 'SiteAdmin') return 'Site';
    if (fullRole === 'OrgAdmin') return 'Org';
    if (fullRole === 'EventPlanner') return 'Planner';
    if (fullRole === 'TeamMember') return 'Member';
    return null;
  });

  ngOnInit(): void {
    void this.refreshIdentity();
    merge(
      this.msalBroadcastService.inProgress$.pipe(filter(status => status === InteractionStatus.None)),
      this.authService.simStateChanged$
    ).pipe(
      takeUntil(this.destroying$)
    ).subscribe(() => {
      void this.refreshIdentity();
    });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private async refreshIdentity(): Promise<void> {
    const account = this.authService.getActiveAccount();
    this.isLoggedIn.set(!!account);
    this.userName.set(account?.name ?? account?.username ?? null);

    if (!account) {
      this.roles.set([]);
      return;
    }

    const roles = await this.authService.getRolesAsync();
    this.roles.set(roles);
  }
}
